/**
 * Receive goods with a quality checkpoint (batch 8 T12c2, #3033): one atomic receipt of the lines that arrived.
 *
 * The user types, per line, what arrived and how it divides into accepted, rejected and held units, and states whether a quality
 * check happened (never defaulted). The form shows the equality rule while typing, a clear summary is reviewed before anything is
 * written, and the request is the one the server's strict schema accepts. Only accepted units become stock: held and rejected units
 * are shown as such and never as received. The write is a keyed command (see `useReceivingOperations`): an ambiguous outcome is held
 * and offered for an explicit retry of the identical request.
 *
 * Standalone: it takes the order lines as props and enforces the owner/target boundary itself.
 */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { useReceivingOperations, type SubmitResult } from '@/hooks/usePurchaseReceipts';
import {
  emptyLineDraft,
  evaluateReceiveForm,
  remainingOf,
  type LineDraft,
  type LineEvaluation,
  type LineProblem,
  type QualityChoice,
} from './receivingArithmetic';
import { blockedMessage, JournalNotice, PendingOperationAlert, ReceivingBoundary, ReceivingErrorAlert } from './receivingParts';
import { slotOf } from './receivingJournal';
import { NOTE_MAX, REFERENCE_MAX, REJECT_REASONS, type ReceiveGoodsResult, type ReceivingLine } from './receivingTypes';

export interface ReceiveGoodsDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string;
  /** Shown in the title; the order's own number, never an id. */
  orderNumber?: string | undefined;
  lines: readonly ReceivingLine[];
  /** Called once with the server's result, only while the session that sent the receipt is still the live one. */
  onReceived?: ((result: ReceiveGoodsResult) => void) | undefined;
}

export function ReceiveGoodsDialog(props: ReceiveGoodsDialogProps) {
  return (
    <ReceivingBoundary target={props.purchaseOrderId} dialog={{ open: props.open, onClose: props.onClose }}>
      <ReceiveGoodsForm {...props} />
    </ReceivingBoundary>
  );
}

type Stage = 'edit' | 'review' | 'done';

const lineLabel = (line: ReceivingLine): string => `${line.partNumber} ${line.name}`.trim();

function ReceiveGoodsForm({ onClose, purchaseOrderId, orderNumber, lines, onReceived }: ReceiveGoodsDialogProps) {
  const { t } = useTranslation('procurementReceiving');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const ops = useReceivingOperations(purchaseOrderId);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<Stage>('edit');
  const [reviewed, setReviewed] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [result, setResult] = useState<ReceiveGoodsResult | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const sending = useRef(false);

  const evaluation = useMemo(
    () => evaluateReceiveForm({ drafts, lines, deliveryNoteReference: reference, note }),
    [drafts, lines, reference, note],
  );
  // The signature is the request AND the facts of its lines the user was shown (ordered, received, held, cancelled): a refetch of
  // the order that changes any of them drops the review in this very render, like an edit of the draft does.
  const signature = evaluation.body
    ? JSON.stringify({
        body: evaluation.body,
        facts: evaluation.body.lines.map((sent) => {
          const line = lines.find((candidate) => candidate.id === sent.lineId);
          return [sent.lineId, line ? [line.quantity, line.quantityReceived, line.quantityHeld ?? null, line.quantityCancelled ?? null] : null];
        }),
      })
    : null;
  // A review belongs to exactly the request it showed: any change of the draft or of the lines drops it in this very render.
  const reviewing = stage === 'review' && signature !== null && signature === reviewed;
  const shown: Stage = stage === 'review' && !reviewing ? 'edit' : stage;

  const slot = slotOf('receive', purchaseOrderId);
  const held = ops.entries.find((entry) => entry.slot === slot) ?? null;
  const pending = ops.isPending(slot);
  const lineById = useMemo(() => new Map(lines.map((line) => [line.id, line])), [lines]);

  const update = (lineId: string, patch: Partial<LineDraft>) => {
    setBlocked(null);
    setRefusal(null);
    setDrafts((current) => ({ ...current, [lineId]: { ...(current[lineId] ?? emptyLineDraft()), ...patch } }));
  };

  const handle = (outcome: SubmitResult) => {
    if (outcome.status === 'success') {
      const received = outcome.result as ReceiveGoodsResult;
      setResult(received);
      setStage('done');
      setRefusal(null);
      notify.success(t('receive.notifyTitle'), t('receive.notifyBody', { number: received.receiptNumber }));
      onReceived?.(received);
    } else if (outcome.status === 'refused') {
      setRefusal(outcome.error);
      setStage('edit');
    } else if (outcome.status === 'blocked') {
      setBlocked(blockedMessage(outcome.reason, t as never));
      setStage('edit');
    } else if (outcome.status === 'uncertain') {
      // The held request shows its own alert; a knowing discard must lead back to a draft that is reviewed again.
      setStage('edit');
    }
    // `busy` and `canceled`: nothing to report to this screen.
  };

  const review = () => {
    setAttempted(true);
    if (signature === null) return;
    setReviewed(signature);
    setStage('review');
  };

  const confirm = async () => {
    if (!reviewing || evaluation.body === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.receive(evaluation.body));
    } finally {
      sending.current = false;
    }
  };

  const retry = async () => {
    if (held === null || sending.current) return;
    sending.current = true;
    try {
      handle(await ops.retry(held));
    } finally {
      sending.current = false;
    }
  };

  const totals = useMemo(() => {
    let accepted = 0;
    let heldUnits = 0;
    let rejected = 0;
    for (const entry of evaluation.byLine.values()) {
      if (!entry.included) continue;
      accepted += entry.accepted ?? 0;
      heldUnits += entry.held ?? 0;
      rejected += entry.rejected ?? 0;
    }
    return { accepted, held: heldUnits, rejected };
  }, [evaluation]);

  const title = orderNumber ? t('receive.titleFor', { number: orderNumber }) : t('receive.title');

  return (
    <ResponsiveDialog open onClose={onClose} fullWidth maxWidth="md" mobileTitle={title} aria-labelledby="receive-goods-title">
      <DialogTitle id="receive-goods-title">{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <JournalNotice status={ops.journalStatus} onDiscardDamaged={ops.discardDamaged} onRecheck={ops.recheckStorage} />
          {!ops.canReceive && <Alert severity="warning">{t('errors.noPermission')}</Alert>}

          {shown === 'done' && result !== null && <ReceiveResult result={result} lineById={lineById} />}

          {shown !== 'done' && held !== null && (
            <PendingOperationAlert
              entry={held}
              error={ops.errorOf(slot)}
              busy={pending}
              journalStatus={ops.journalStatus}
              onRetry={() => void retry()}
              onDiscard={() => ops.discard(held)}
            />
          )}

          {shown !== 'done' && held === null && shown === 'edit' && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('receive.intro')}
              </Typography>
              {lines.map((line) => (
                <LineEditor
                  key={line.id}
                  line={line}
                  draft={drafts[line.id] ?? emptyLineDraft()}
                  evaluation={evaluation.byLine.get(line.id)}
                  showProblems={attempted}
                  onChange={(patch) => update(line.id, patch)}
                />
              ))}
              <TextField
                size="small"
                label={t('receive.deliveryNote')}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                slotProps={{ htmlInput: { maxLength: REFERENCE_MAX } }}
              />
              <TextField
                size="small"
                multiline
                minRows={2}
                label={t('receive.note')}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
              />
              {attempted && evaluation.formProblems.includes('noLines') && <Alert severity="error">{t('receive.problems.noLines')}</Alert>}
              {attempted && signature === null && !evaluation.formProblems.includes('noLines') && <Alert severity="error">{t('receive.problems.fixLines')}</Alert>}
            </>
          )}

          {shown === 'review' && held === null && evaluation.body !== null && (
            <ReceiveSummary evaluation={evaluation.byLine} lines={lines} drafts={drafts} totals={totals} reference={reference} note={note} />
          )}

          <ReceivingErrorAlert error={refusal} />
          {blocked !== null && <Alert severity="error">{blocked}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {shown === 'done' ? (
          <Button onClick={onClose} variant="contained">
            {tc('close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>{tc('cancel')}</Button>
            {held === null && shown === 'review' && <Button onClick={() => setStage('edit')}>{tc('back')}</Button>}
            {held === null && shown === 'edit' && (
              <Button variant="contained" onClick={review} disabled={!ops.canReceive || ops.journalStatus !== 'ok'}>
                {t('receive.review')}
              </Button>
            )}
            {held === null && shown === 'review' && (
              <Button variant="contained" onClick={() => void confirm()} disabled={pending || !ops.canReceive || ops.journalStatus !== 'ok'}>
                {t('receive.confirm')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------------------

interface LineEditorProps {
  line: ReceivingLine;
  draft: LineDraft;
  evaluation: LineEvaluation | undefined;
  showProblems: boolean;
  onChange: (patch: Partial<LineDraft>) => void;
}

function LineEditor({ line, draft, evaluation, showProblems, onChange }: LineEditorProps) {
  const { t } = useTranslation('procurementReceiving');
  const label = lineLabel(line);
  const remaining = remainingOf(line);
  const unobserved = draft.quality === 'unobserved';
  const rejectedUnits = evaluation?.rejected ?? 0;
  const problems = evaluation?.problems ?? [];
  const visible = problems.filter((problem) => showProblems || problem === 'splitMismatch');
  const number = (key: keyof Pick<LineDraft, 'arrived' | 'accepted' | 'rejected' | 'held'>, name: string, disabled = false) => (
    <TextField
      size="small"
      label={t(`receive.fields.${key}`)}
      value={draft[key]}
      disabled={disabled}
      onChange={(event) => onChange({ [key]: event.target.value })}
      slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': t(`receive.fields.${key}For`, { part: name }) } }}
      sx={{ width: 110 }}
    />
  );
  return (
    <Paper variant="outlined" sx={{ p: 2 }} role="group" aria-label={label}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">{label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('receive.lineFacts', { ordered: line.quantity, received: line.quantityReceived })}
            {remaining !== null && ` ${t('receive.lineRemaining', { remaining })}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {number('arrived', label)}
          {number('accepted', label)}
          {number('rejected', label, unobserved)}
          {number('held', label, unobserved)}
          {rejectedUnits > 0 && !unobserved && (
            <TextField
              select
              size="small"
              required
              label={t('receive.fields.reason')}
              value={draft.reason}
              onChange={(event) => onChange({ reason: event.target.value as LineDraft['reason'] })}
              sx={{ minWidth: 180 }}
            >
              {REJECT_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {t(`rejectReason.${reason}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        <FormControl>
          <FormLabel id={`quality-${line.id}`}>{t('receive.quality.label')}</FormLabel>
          <RadioGroup
            row
            aria-labelledby={`quality-${line.id}`}
            value={draft.quality === 'unset' ? '' : draft.quality}
            onChange={(event) => {
              const quality = event.target.value as QualityChoice;
              // Without a quality check nothing can be recorded as rejected or held.
              onChange(quality === 'unobserved' ? { quality, rejected: '', held: '', reason: '' } : { quality });
            }}
          >
            <FormControlLabel value="observed" control={<Radio size="small" />} label={t('receive.quality.observed')} />
            <FormControlLabel value="unobserved" control={<Radio size="small" />} label={t('receive.quality.unobserved')} />
          </RadioGroup>
          {unobserved && (
            <Typography variant="caption" color="text.secondary">
              {t('receive.quality.unobservedHint')}
            </Typography>
          )}
        </FormControl>
        {evaluation?.included === true && (
          <Typography variant="caption" color={evaluation.splitSum === null ? 'text.secondary' : 'error'} role={evaluation.splitSum === null ? undefined : 'alert'}>
            {t('receive.equality', {
              accepted: evaluation.accepted ?? 0,
              rejected: evaluation.rejected ?? 0,
              held: evaluation.held ?? 0,
              arrived: evaluation.arrived ?? 0,
            })}
            {evaluation.splitSum !== null && ` ${t('receive.problems.splitMismatch', { sum: evaluation.splitSum, arrived: evaluation.arrived ?? 0 })}`}
          </Typography>
        )}
        {remaining !== null && evaluation?.arrived != null && evaluation.arrived > remaining && (
          <Typography variant="caption" color="warning.main">
            {t('receive.exceedsRemaining', { remaining })}
          </Typography>
        )}
        {visible
          .filter((problem) => problem !== 'splitMismatch')
          .map((problem: LineProblem) => (
            <Typography key={problem} variant="caption" color="error" role="alert">
              {t(`receive.problems.${problem}`)}
            </Typography>
          ))}
        <TextField
          size="small"
          label={t('receive.lineNote')}
          value={draft.note}
          onChange={(event) => onChange({ note: event.target.value })}
          slotProps={{ htmlInput: { maxLength: NOTE_MAX, 'aria-label': t('receive.lineNoteFor', { part: label }) } }}
        />
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------

interface SummaryProps {
  evaluation: ReadonlyMap<string, LineEvaluation>;
  lines: readonly ReceivingLine[];
  drafts: Readonly<Record<string, LineDraft>>;
  totals: { accepted: number; held: number; rejected: number };
  reference: string;
  note: string;
}

function ReceiveSummary({ evaluation, lines, drafts, totals, reference, note }: SummaryProps) {
  const { t } = useTranslation('procurementReceiving');
  const included = lines.filter((line) => evaluation.get(line.id)?.included === true);
  const unobservedLines = included.filter((line) => drafts[line.id]?.quality === 'unobserved').length;
  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <strong>{t('receive.summary.title')}</strong> {t('receive.summary.totals', totals)}
        <br />
        {t('receive.summary.stockRule')}
      </Alert>
      {unobservedLines > 0 && <Alert severity="warning">{t('receive.summary.unobserved', { count: unobservedLines })}</Alert>}
      <Table size="small" aria-label={t('receive.summary.tableLabel')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('columns.part')}</TableCell>
            <TableCell align="right">{t('columns.arrived')}</TableCell>
            <TableCell align="right">{t('columns.toStock')}</TableCell>
            <TableCell align="right">{t('columns.held')}</TableCell>
            <TableCell align="right">{t('columns.rejected')}</TableCell>
            <TableCell>{t('columns.quality')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {included.map((line) => {
            const entry = evaluation.get(line.id);
            const draft = drafts[line.id];
            return (
              <TableRow key={line.id}>
                <TableCell>{lineLabel(line)}</TableCell>
                <TableCell align="right">{entry?.arrived}</TableCell>
                <TableCell align="right">{entry?.accepted}</TableCell>
                <TableCell align="right">{entry?.held}</TableCell>
                <TableCell align="right">
                  {entry?.rejected}
                  {(entry?.rejected ?? 0) > 0 && draft && draft.reason !== '' && ` (${t(`rejectReason.${draft.reason}`)})`}
                </TableCell>
                <TableCell>{draft?.quality === 'observed' ? t('receive.quality.observed') : t('receive.quality.unobserved')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {reference.trim() !== '' && (
        <Typography variant="body2">
          {t('receive.deliveryNote')}: {reference.trim()}
        </Typography>
      )}
      {note.trim() !== '' && (
        <Typography variant="body2">
          {t('receive.note')}: {note.trim()}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {t('receive.summary.confirmHint')}
      </Typography>
    </Stack>
  );
}

function ReceiveResult({ result, lineById }: { result: ReceiveGoodsResult; lineById: ReadonlyMap<string, ReceivingLine> }) {
  const { t } = useTranslation('procurementReceiving');
  const stock = result.lines.reduce((sum, line) => sum + line.quantityAccepted, 0);
  return (
    <Stack spacing={2}>
      <Alert severity="success">{t('receive.done.title', { number: result.receiptNumber })}</Alert>
      {result.replayed && <Alert severity="info">{t('receive.done.replayed')}</Alert>}
      <Alert severity={stock > 0 ? 'info' : 'warning'}>{stock > 0 ? t('receive.done.stock', { count: stock }) : t('receive.done.noStock')}</Alert>
      <Table size="small" aria-label={t('receive.done.tableLabel')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('columns.part')}</TableCell>
            <TableCell align="right">{t('columns.toStock')}</TableCell>
            <TableCell align="right">{t('columns.held')}</TableCell>
            <TableCell align="right">{t('columns.rejected')}</TableCell>
            <TableCell align="right">{t('columns.receivedTotal')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.lines.map((line) => {
            const source = lineById.get(line.lineId);
            return (
              <TableRow key={line.lineId}>
                <TableCell>{source ? lineLabel(source) : t('history.unknownLine')}</TableCell>
                <TableCell align="right">{line.quantityAccepted}</TableCell>
                <TableCell align="right">{line.qualityObserved ? line.quantityHeld : '—'}</TableCell>
                <TableCell align="right">{line.qualityObserved ? line.quantityRejected : '—'}</TableCell>
                <TableCell align="right">{`${line.quantityReceivedTotal} / ${line.quantityOrdered}`}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography variant="body2">{result.fullyReceived ? t('receive.done.fullyReceived') : t('receive.done.notFullyReceived')}</Typography>
      {result.replayed && <Typography variant="caption">{t('receive.done.currentCounters')}</Typography>}
    </Stack>
  );
}
